import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { type Campaign, CampaignClass, type Goal as GoalModel } from '@feathr/blackbox';
import {
  Button,
  ButtonValid,
  CardContent,
  CardHeader,
  CardV2 as Card,
  Form,
  SegmentedControl,
  Well,
} from '@feathr/components';
import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useRole, useStore } from '@feathr/extender/state';
import { flattenErrors } from '@feathr/hooks';

import AddGoals from './AddGoals';
import { getGoalSegments, validateStepGoals } from './StepGoals.utils';

import * as styles from '../AdWizard/AdWizard.css';

interface IButtonProps {
  goals: IObservableArray<GoalModel>;
  onNext?: () => void;
}

interface IProps extends IButtonProps {
  onPrev?: () => void;
  campaign: Campaign;
  disabled?: boolean;
  goalsVersion?: 'v1' | 'v2';
  onGoalsVersionChange?: (version: 'v1' | 'v2') => void;
}

const NextStepButton = observer(({ goals, onNext }: IButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const { Segments } = useStore();
  const segments = getGoalSegments(goals, Segments);
  const validationErrors = validateStepGoals({ goals, goalSegments: segments });
  return (
    <ButtonValid errors={flattenErrors(validationErrors)} name={'next_step'} onClick={onNext}>
      {t('Next')}
    </ButtonValid>
  );
});

function StepGoals({
  campaign,
  disabled = false,
  goals,
  goalsVersion = 'v1',
  onGoalsVersionChange,
  onNext,
  onPrev,
}: IProps): JSX.Element {
  const { t } = useTranslation();
  const { isSuperuser } = useRole();
  const { isOnProduction } = useBlackboxVersion();

  const hasVerticalWizard = [
    CampaignClass.Referral,
    CampaignClass.TrackedLink,
    CampaignClass.SmartPinpointEmail,
  ].includes(campaign.get('_cls'));

  const actions = hasVerticalWizard
    ? [
        <Button key={'prev'} name={'previous_step'} onClick={onPrev}>
          {t('Previous')}
        </Button>,
        <NextStepButton goals={goals} key={'next'} onNext={onNext} />,
      ]
    : [];

  const goalsDescription = (
    <Trans t={t}>
      <p>
        Select a goal for this campaign. In Goal mode, track conversions when individuals who engage
        with this campaign join a group. In Advanced mode, track conversions using a Conversion
        Pixel to monitor people interacting with the campaign.
      </p>
      <p>
        <a
          href={'https://help.feathr.co/hc/en-us/articles/360047216094-What-is-a-Goal'}
          target={'_blank'}
        >
          Learn more about goal tracking
        </a>
      </p>
    </Trans>
  );

  const data = [
    { label: t('Goals 1.0'), value: 'v1' },
    { label: t('Goals 2.0'), value: 'v2' },
  ];

  return (
    <Form
      actions={actions}
      className={hasVerticalWizard ? undefined : styles.formRoot}
      label={t('Edit Campaign: Goals')}
      width={'wide'}
    >
      <Card name={'goals-card'} width={'wide'}>
        <CardHeader description={goalsDescription} title={t('Set Goals')}>
          {!isOnProduction && isSuperuser && (
            <Well
              description={t('Defaults to the value of the showGoalsV2 flag')}
              label={t('Goals version (Dev & QA)')}
              layout={'vertical'}
            >
              <SegmentedControl
                data={data}
                onChange={(value): void => onGoalsVersionChange?.(value as 'v1' | 'v2')}
                value={goalsVersion}
              />
            </Well>
          )}
        </CardHeader>
        <CardContent addVerticalGap={true}>
          <AddGoals campaign={campaign} disabled={disabled} goals={goals} version={goalsVersion} />
        </CardContent>
      </Card>
    </Form>
  );
}

export default observer(StepGoals);
